import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ProfileSelectorComponent } from '@app/components/profile/profile-selector/profile-selector.component';
import { LegalQualificationSummaryDialogComponent } from '@app/components/profile/legal-qualification-summary-dialog/legal-qualification-summary-dialog.component';
import { ProfileProjectsTabComponent } from '@app/components/profile/projects-tab/projects-tab.component';
import { AuthStore } from '@app/stores/auth.store';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';

export type ProfileMenu =
  | 'personalInfo'
  | 'personalInfo.legalPerson'
  | 'personalInfo.representative'
  | 'personalInfo.documents'
  | 'myProjects'
  | 'myProjects.fundraising'
  | 'myProjects.submitted'
  | 'myProjects.inPreparation'
  | 'myProjects.underReview'
  | 'investments'
  | 'investments.accepted'
  | 'investments.pending'
  | 'investments.rejected'
  | 'watchedProjects'
  | 'complaint'
  | 'message';

@Component({
  selector: 'app-legal-person-profile',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ProfileSelectorComponent,
    ProfileProjectsTabComponent,
    NgbCollapseModule,
    RouterModule,
  ],
  templateUrl: './legal-person-profile.component.html',
  styleUrl: './legal-person-profile.component.scss',
})
export class LegalPersonProfileComponent implements OnInit {
  protected authStore = inject(AuthStore);
  private modalService = inject(NgbModal);
  selectedMenu = signal<ProfileMenu>('personalInfo.legalPerson');

  public isPersonalInfoCollapsed = signal(true);
  public isMyProjectsCollapsed = signal(true);
  public isInvestmentsCollapsed = signal(true);
  public isWatchedProjectsCollapsed = signal(true);

  constructor(
    private router: Router,
    private destroyRef: DestroyRef,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        const urlSegments = url.split('/');
        const lastSegment = urlSegments[urlSegments.length - 1] as ProfileMenu;
        if (this.isValidMenuType(lastSegment)) {
          this.selectedMenu.set(lastSegment);
        }
      });
    this.setInitialCollapseState();
    effect(() => {
      this.setInitialCollapseState();
    });

  }

  ngOnInit(): void {
    this.updateMenuFromCurrentUrl();
  }

  private setInitialCollapseState() {
    const menu = this.selectedMenu();
    const menuGroup = menu.split('.')[0];
    this.isPersonalInfoCollapsed.set(menuGroup !== 'personalInfo');
    this.isMyProjectsCollapsed.set(menuGroup !== 'myProjects');
    this.isInvestmentsCollapsed.set(menuGroup !== 'investments');
    this.isWatchedProjectsCollapsed.set(menuGroup !== 'watchedProjects');
  }

  navigateToQualification() {
    this.router.navigate(['investor-qualification-test']);
  }

  isProfessionalClient(): boolean {
    return this.authStore.loggedInCompany()?.professionalCertificate === true;
  }

  hasQualification(): boolean {
    return this.authStore.loggedInCompany()?.stepInvestorQualification === true;
  }

  onQualificationButtonClick(): void {
    if (this.isProfessionalClient()) {
      return;
    }

    if (!this.hasQualification()) {
      this.navigateToQualification();
      return;
    }

    const dialogRef = this.modalService.open(
      LegalQualificationSummaryDialogComponent,
      {
        centered: true,
        size: 'lg',
        scrollable: true,
      },
    );
    dialogRef.componentInstance.entity = this.authStore.loggedInCompany() ?? null;

    dialogRef.result.then(
      (result) => {
        if (result === 'requalify') {
          this.navigateToQualification();
        }
      },
      () => undefined,
    );
  }

  selectMenu(menu: ProfileMenu) {
    this.selectedMenu.set(menu);
    this.router.navigate(['profile', 'legal', menu]);
  }

  toggleCollapse(
    menuGroup:
      | 'personalInfo'
      | 'myProjects'
      | 'investments'
      | 'watchedProjects',
  ) {
    if (menuGroup === 'personalInfo')
      this.isPersonalInfoCollapsed.update((v) => !v);
    if (menuGroup === 'myProjects')
      this.isMyProjectsCollapsed.update((v) => !v);
    if (menuGroup === 'investments')
      this.isInvestmentsCollapsed.update((v) => !v);
    if (menuGroup === 'watchedProjects')
      this.isWatchedProjectsCollapsed.update((v) => !v);
  }

  private updateMenuFromCurrentUrl() {
    const urlSegments = this.router.url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1] as ProfileMenu;
    if (this.isValidMenuType(lastSegment)) {
      this.selectedMenu.set(lastSegment);
    }
  }

  private isValidMenuType(value: string): value is ProfileMenu {
    return [
      'personalInfo.legalPerson',
      'personalInfo.representative',
      'personalInfo.documents',
      'myProjects.fundraising',
      'myProjects.submitted',
      'myProjects.inPreparation',
      'myProjects.underReview',
      'investments.accepted',
      'investments.pending',
      'investments.rejected',
      'watchedProjects',
      'complaint',
      'message',
    ].includes(value);
  }

}
